import React, { useState, useEffect } from "react";
import {
  Table,
  TableCell,
  TableRow,
  TableContainer,
  TableHead,
  TableBody,
  Paper,
  makeStyles,
  createStyles,
  Theme,
  Grid,
  Button,
} from "@material-ui/core";
import Checkbox from "@material-ui/core/Checkbox";
import { Loading } from "../../components/Loading";
import { ISource, IUserSource } from "../../core/redux/dataMap/source";
import AddIcon from "@material-ui/icons/Add";
import { SearchField } from "../../components/SearchField";
import { SourcesModal } from "./SourcesModal";
import useLanguage from "../../hooks/useLanguage";

interface IInternetSourcesProps {
  sources: ISource[];
  userSources: IUserSource[];
  isAllChecked: boolean;
  isAllUserSourcesChecked: boolean;
  isLoading: boolean;
  requestSourceModal(): void;
  updateUserSource(source: ISource): void;
  toggleAllSources(): void;
  toggleUserSources(userSource: IUserSource): void;
  toggleAllUserSources(): void;
}

export const InternetSources: React.FC<IInternetSourcesProps> = ({
  sources,
  userSources,
  isLoading,
  isAllChecked,
  isAllUserSourcesChecked,
  toggleAllUserSources,
  updateUserSource,
  toggleAllSources,
  toggleUserSources,
}) => {
  const [searchName, setSearchName] = useState("");
  const [showSourceModal, setShowSourceModal] = useState(false);
  const [filteredSources, setFilteredSources] = useState(userSources);
  const classes = useStyles();
  const { language } = useLanguage();
  const { translate } = language;

  useEffect(() => {
    setFilteredSources(userSources);
  }, [userSources]);

  const filterSources = (searchNameValue) => {
    setFilteredSources(
      userSources.filter((userSource) =>
        userSource.source.name
          .toLocaleLowerCase()
          .includes(searchNameValue.toLocaleLowerCase())
      )
    );
    setSearchName(searchNameValue);
  };

  const submitFilterSources = () => {
    setFilteredSources(
      userSources.filter((userSource) =>
        userSource.source.name
          .toLocaleLowerCase()
          .includes(searchName.toLocaleLowerCase())
      )
    );
  };

  return (
    <div>
      <Grid container justify="space-between" className={classes.header}>
        <Grid item>
          <SearchField
            label={translate.simple_search_placeholder}
            value={searchName}
            onChange={(value) => filterSources(value)}
            onSubmit={submitFilterSources}
            color={false}
          />
        </Grid>
        <Grid item>
          <Button
            onClick={() => setShowSourceModal(true)}
            variant="contained"
            color="primary"
          >
            <AddIcon /> {translate.add}
          </Button>
        </Grid>
      </Grid>
      <Paper elevation={1}>
        <TableContainer className={classes.tabContainer}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={isAllUserSourcesChecked}
                    onChange={() => toggleAllUserSources()}
                    color="secondary"
                  />
                </TableCell>
                <TableCell></TableCell>
                <TableCell>{translate.source}</TableCell>
                <TableCell>{translate.description}</TableCell>
                <TableCell>{translate.source_type}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={5}>
                    <Loading />
                  </TableCell>
                </TableRow>
              ) : (
                filteredSources &&
                filteredSources.map((userSource, index) => {
                  return (
                    <TableRow
                      hover
                      onClick={() => toggleUserSources(userSource)}
                      role="checkbox"
                      aria-checked={userSource.isSelected}
                      tabIndex={-1}
                      key={index}
                      selected={userSource.isSelected}
                      className={classes.row}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={userSource.isSelected}
                          color="secondary"
                        />
                      </TableCell>
                      <TableCell>
                        {!!userSource.source.logo &&
                          userSource.source.logo !== null && (
                            <img
                              src={userSource.source.logo}
                              width="50"
                              height="50"
                              alt="sourceLogo"
                            />
                          )}
                      </TableCell>
                      <TableCell align="left">
                        {userSource.source.name}
                      </TableCell>
                      <TableCell align="left">
                        {userSource.source.description}
                      </TableCell>
                      <TableCell align="left">
                        {userSource.source.type}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      <SourcesModal
        sources={sources}
        show={showSourceModal}
        onHide={() => setShowSourceModal(false)}
        isAllChecked={isAllChecked}
        isLoading={isLoading}
        updateUserSource={updateUserSource}
        toggleAllSources={toggleAllSources}
      />
    </div>
  );
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: "flex",
    },
    header: {
      margin: "20px 0",
    },
    tabContainer: {
      maxHeight: "calc(100vh - 240px)",
      overflow: "auto",
    },
    row: {
      cursor: "pointer",
    },
  })
);
